import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { equal } from '@ember/object/computed';
import { later } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { ApplicationFlowUpdate } from '@square/dashboard-protos/models/squareup/sfs/external/models/application_flow_update';
import { ApplicationFlow } from '@square/dashboard-protos/models/squareup/sfs/external/models/application_flow';
import { scrollToFirstError } from '@square/ember-prism/utils/error-scrolling';
import { defaultScrollable } from '@square/ember-prism/utils/scrolling';
import { AGREEMENTS_URLS } from '@banking/ui/utils/constants';
import { Error } from '@square/dashboard-protos/models/squareup/connect/v2/resources/error';
import * as Model from '@square/dashboard-protos/models/squareup/legalentities/model/model';
import { maskTinLast4 } from '@square/ember-prism/components/prism/input-tin';
import { returnToFlowAction } from '@banking/ui/workflows/onboarding-kyb/action';
import { feature } from '@square/ember-features/macros';

const { IndividualUpdate } = ApplicationFlowUpdate;
const { BackupWithholdingStatus } = IndividualUpdate;

export default class AgreementController extends Controller {
  @service currentApplicationFlow;
  @service intl;
  @service router;
  @service features;
  @service kybWorkflow;

  @tracked isLoading = false;
  @tracked showValidation = false;
  @tracked withholdingStatus = BackupWithholdingStatus.NO_WITHHOLDING;
  @tracked agreementChecked = null;
  @tracked taxId = null;
  @tracked ssnFailedServerValidation = false;
  @tracked showConfirmationModal = false;

  @feature('banking/stop_prefilling_savings_onboarding') stopPrefilling;

  BackupWithholdingStatus = BackupWithholdingStatus;

  @equal(
    'currentApplicationFlow.business.businessType',
    Model.Business.Structure.Type.SOLE_PROPRIETORSHIP
  )
  isSoleProp;

  @equal('withholdingStatus', BackupWithholdingStatus.HAS_WITHHOLDING)
  isSubjectToBackupWithholding;

  @action goBack() {
    this.kybWorkflow.emit(returnToFlowAction());
  }

  get showSolePropFields() {
    return this.isSoleProp && !this.stopPrefilling;
  }

  get maskedSsn() {
    return maskTinLast4(
      this.currentApplicationFlow.individual?.ssnLast4,
      true,
      false
    );
  }

  get maskedBusinessTin() {
    return maskTinLast4(
      this.currentApplicationFlow.business?.businessEinLast4,
      false,
      true
    );
  }

  @computed(
    'isSoleProp',
    'isSsnValid',
    'ssnFailedServerValidation',
    'stopPrefilling',
    'taxId'
  )
  get validationTaxIdFields() {
    if (!this.isSoleProp || this.stopPrefilling) {
      return { isInvalid: false };
    }
    if (this.ssnFailedServerValidation) {
      return {
        isInvalid: true,
        message: this.intl.t(
          'sfs.signup.agreement.taxId.validationSsnMismatch'
        ),
      };
    } else if (!this.isSsnValid) {
      return {
        isInvalid: true,
        message: this.intl.t(
          'sfs.signup.agreement.taxId.validationSsnRequired'
        ),
      };
    } else {
      return { isInvalid: false };
    }
  }

  get isSsnValid() {
    return this.taxId
      ?.trim()
      .replace(/[•-]/g, '')
      .match(/^\d{9}$/);
  }

  @computed('withholdingStatus')
  get validationWithholdingStatus() {
    return {
      isInvalid: this.withholdingStatus === null,
      message: this.intl.t(
        'sfs.signup.agreement.taxCertification.withholdingStatusValidation'
      ),
    };
  }

  @computed('agreementChecked')
  get validationAgreementChecked() {
    return {
      isInvalid: this.agreementChecked !== true,
      message: this.intl.t(
        'sfs.signup.agreement.disclosures.agreementValidation'
      ),
    };
  }

  isValid() {
    return !(
      this.validationTaxIdFields.isInvalid ||
      this.validationWithholdingStatus.isInvalid ||
      this.validationAgreementChecked.isInvalid
    );
  }

  get individualUpdate() {
    const individual = new IndividualUpdate.Builder()
      .id(this.currentApplicationFlow.individual?.id)
      .backupWithholdingStatus(this.withholdingStatus)
      .isManager(this.currentApplicationFlow.individual?.isManager)
      .hasSignificantOwnership(
        this.currentApplicationFlow.individual?.hasSignificantOwnership
      );

    if (this.isSoleProp) {
      individual.ssn(this.taxId);
    }

    return individual.build();
  }

  get applicationFlowUpdate() {
    return new ApplicationFlowUpdate.Builder()
      .id(this.currentApplicationFlow.id)
      .individual(this.individualUpdate)
      .build();
  }

  hasSsnFieldError(error) {
    return error?.protoMessage?.errors?.find((e) => {
      return (
        e.code === Error.Code.INVALID_VALUE &&
        e.category === Error.Category.INVALID_REQUEST_ERROR &&
        e.field === 'application_flow.individual.ssn'
      );
    });
  }

  @action submit(event) {
    event.preventDefault();

    // reset server check so that isValid() should pass:
    this.ssnFailedServerValidation = false;

    if (this.isValid()) {
      if (this.withholdingStatus === BackupWithholdingStatus.HAS_WITHHOLDING) {
        this.showConfirmationModal = true;
      } else {
        this.submitApplicationFlow();
      }
    } else {
      this.revealValidations();
    }
  }

  @action confirm(event) {
    event.preventDefault();
    this.submitApplicationFlow();
  }

  async submitApplicationFlow() {
    this.showConfirmationModal = false;

    const businessType = this.kybWorkflow.state.businessType;
    // The UI is set up for individual business types to submit SSNs,
    // and every other business type uses EINs.
    const taxTinType =
      businessType === 'individual'
        ? ApplicationFlow.TaxTinType.SSN
        : ApplicationFlow.TaxTinType.EIN;

    this.isLoading = true;
    try {
      await this.currentApplicationFlow.submit(
        this.applicationFlowUpdate,
        taxTinType
      );
      this.replaceRoute('signup.please-wait');
    } catch (error) {
      if (this.hasSsnFieldError(error)) {
        this.ssnFailedServerValidation = true;
        document.querySelector('#tax-id-number')?.focus();
        this.revealValidations();
      } else {
        this.send('handleRpcError', error);
      }
    } finally {
      this.isLoading = false;
    }
  }

  revealValidations() {
    this.showValidation = true;
    // This is in a runloop because the error classes have not yet rendered
    later(() => {
      scrollToFirstError(defaultScrollable());
    });
  }

  get depositAccountLink() {
    return AGREEMENTS_URLS.depositAccountLink;
  }

  get privacyPolicyLink() {
    return AGREEMENTS_URLS.privacyPolicyLink;
  }

  get eSignAgreementLink() {
    return AGREEMENTS_URLS.eSignAgreementLink;
  }

  get termsOfServiceLink() {
    return AGREEMENTS_URLS.termsOfServiceLink;
  }
}
